/**
 *  Copyright AlpineReplay Inc, 2021. All rights reserved.
 *  Authors: Brian Bal, Leonid Khramov
 */
import { valueFor } from "./helpers"
import { getProperty } from "trux"
import {
    formatFullName,
    formatFirstName,
    formatLastName,
} from "../trace/formatters"

export const getPlaylist = (playlistId, state) => {
    var p = {
        id: "coach-notes",
        title: "Coach Notes",
        enabled: true,
        scopes: [],
        event_count: 0,
        filters: [
            { property: "event.type", op: "equal", constant: "CoachAudio" },
        ],
    }
    for (var i = 0; i < state.game.playlists.length; i++) {
        var ply = state.game.playlists[i]
        if (ply.id === playlistId) {
            p = ply
            break
        }
    }
    return p
}

export const getAllPlaylists = (state, onlyEnabled = true) => {
    return state.game.playlists.filter((f) => {
        var keep = true
        if (onlyEnabled) {
            keep = f.enabled || f.show_when_empty
        }
        return keep
    })
}

export const getPlayersPositions = (innings) => {
    let playersArray = []
    Object.entries(innings).forEach(([inning, positions]) => {
        Object.entries(positions).forEach(([position, player]) => {
            let foundPlayer = playersArray.find((p) => p.traceNumber === player)
            if (foundPlayer) {
                let foundPlayersPosition = positionsMapping(position)
                if (
                    !foundPlayer.positions.find(
                        (fp) => fp === foundPlayersPosition
                    )
                ) {
                    foundPlayer.positions.push(foundPlayersPosition)
                }
            } else {
                let newPlayer = {
                    traceNumber: player,
                    positions: [positionsMapping(position)],
                }
                playersArray.push(newPlayer)
            }
        })
    })
    return playersArray
}

export const positionsMapping = (position) => {
    switch (position) {
        case "home_plate":
            return "C"
        case "first_base":
            return "1B"
        case "second_base":
            return "2B"
        case "third_base":
            return "3B"
        case "shortstop":
            return "SS"
        case "pitching_mound":
            return "P"
        case "left_field":
            return "LF"
        case "center_field":
            return "CF"
        case "right_field":
            return "RF"
        default:
            return "-"
    }
}

function filterOne(filter, scope) {
    var sourceVal = valueFor(filter.property, "", scope)
    var scopeVal
    if (Object.prototype.hasOwnProperty.call(filter, "constant")) {
        scopeVal = filter.constant
    } else if (Object.prototype.hasOwnProperty.call(filter, "scope_property")) {
        scopeVal = valueFor(filter.scope_property, "", scope)
    } else {
        scopeVal = ""
    }

    var keep = false
    switch (filter.op) {
        case "equal":
            //here we use == intentionally as we don't want to compare types
            keep = sourceVal == scopeVal // eslint-disable-line eqeqeq
            break
        case "less_than":
            keep = sourceVal < scopeVal
            break
        case "greater_than":
            keep = sourceVal > scopeVal
            break
        case "includes":
            keep = sourceVal.includes(scopeVal)
            break
        case "not_includes":
            keep = !sourceVal.includes(scopeVal)
            break
        case "includes_one":
            keep = false
            if (Array.isArray(scopeVal)) {
                for (let sval of scopeVal) {
                    keep = keep || sourceVal.includes(sval)
                }
            }
            break
        case "tagged":
            keep = tagFilter(sourceVal, scopeVal)
            break
        case "not_tagged":
            keep = !tagFilter(sourceVal, scopeVal)
            break
        case "camera":
            // Because of the way this needs to be checked for it breaks convention
            keep = cameraFilter(scope.event, scopeVal)
            break
        default:
            keep = false
    }
    return keep
}

function cameraFilter(event, camera) {
    let found = false
    if (getProperty("meta.camera.name", event, "") === camera) return true
    if (event.sources) {
        event.sources.some((source) => {
            if (getProperty("meta.camera.name", source, "") === camera) {
                found = true
                return true
            }
            return false
        })
    }
    return found
}

function tagFilter(tags, filters) {
    let keep = true
    for (let filter of filters) {
        let found = false
        for (let tag of tags) {
            if (tag.name === filter) {
                found = true
            }
        }
        if (!found) {
            keep = false
        }
    }
    return keep
}

function fillEventTitles(events, playlist) {
    // eslint-disable-next-line eqeqeq
    let scope = playlist.scope != null ? playlist.scope : {}
    let titles = playlist.event_title
    events.forEach((event) => {
        scope.event = event

        //restore original title
        event.title = event.orig_title

        //find and render new title among playlist titles
        for (let i in titles) {
            let title = titles[i]
            if (title.required !== undefined) {
                let keep = true
                for (let j in title.required) {
                    let filter = title.required[j]
                    keep &= filterOne(filter, scope)
                }

                //One of the filters failed -> skip this title
                if (!keep) continue
            }
            let dynamicTitle = renderString(title.title, scope)
            if (dynamicTitle.trim().length > 0) {
                event.title = dynamicTitle
            }
            break
        }

        if (event.orig_type && event.orig_type === "goalkeeper") {
            event.title = "Action around the box"
        }
    })
}

function cameraSortIndex(camera) {
    if (camera.name === "multi-cam") return 5
    if (camera.name === "tracecam") return 10
    return 20 //
}

export const filterEventsWithPlaylist = (
    playlist,
    events,
    otherFilters = [],
    selectedOrder = "time"
) => {
    // eslint-disable-next-line eqeqeq
    if (playlist == null) {
        return events
    }
    // example filter:
    // { "property": "event.type", "op": "equal", "constant": "FullGameVideo" }
    var filters = []
    for (var i in playlist.filters) {
        filters.push(playlist.filters[i])
    }
    for (i in otherFilters) {
        filters.push(otherFilters[i])
    }

    // eslint-disable-next-line eqeqeq
    var scope = playlist.scope != null ? playlist.scope : {}

    // optional
    let optional = true
    for (const f of filters) {
        optional = optional && !!f.optional
    }

    var filteredEvents = []
    for (let ev of events) {
        let allPassed = true
        let onePassed = false

        for (const f of filters) {
            scope["event"] = ev
            let keep = !!filterOne(f, scope)
            if (keep) {
                onePassed = true
            } else {
                allPassed = false
            }
        }

        if (optional) {
            if (onePassed) {
                filteredEvents.push(ev)
            }
        } else {
            if (allPassed) {
                filteredEvents.push(ev)
            }
        }
    }

    //if we have event title template in the playlist, let's apply it to all events titles
    //even if we erase the event.title we still have the original title in event.orig_title
    if (playlist.event_title !== undefined) {
        fillEventTitles(filteredEvents, playlist)
    }
    filteredEvents = filteredEvents.sort((a, b) => {
        let delta = a.full_game_time - b.full_game_time
        if (a.meta.grp && b.meta.grp) {
            delta = a.meta.grp.localeCompare(b.meta.grp)
        }
        if (delta === 0 && a.meta.camera && b.meta.camera) {
            //sort by camera if the time is the same
            let aIdx = cameraSortIndex(a.meta.camera)
            let bIdx = cameraSortIndex(b.meta.camera)
            delta = aIdx - bIdx
            if (delta === 0)
                delta = a.meta.camera.title.localeCompare(b.meta.camera.title)
        }
        return delta
    })

    switch (selectedOrder) {
        case "baseball-tags":
            filteredEvents = filteredEvents.sort((a, b) => {
                let aVal = getBaseballTagsSortValue(a)
                let bVal = getBaseballTagsSortValue(b)
                return aVal - bVal
            })
            break
        // next custom sort should go here
    }

    // create display number
    let displayNumber = 0
    for (let fev of filteredEvents) {
        displayNumber++
        fev.display_number = displayNumber
    }

    return filteredEvents
}

const findOrAddTagFilter = (filters, newFilter) => {
    let tagFilter = filters.find(
        // eslint-disable-next-line eqeqeq
        (f) => f.property == newFilter.property && f.op == newFilter.op
    )
    if (tagFilter === undefined) {
        filters.push(newFilter)
    } else {
        tagFilter.constant = [...tagFilter.constant, ...newFilter.constant]
    }
}

export const buildFilters = (stateFilters) => {
    let filters = []

    if (stateFilters.selectedHalf && stateFilters.selectedHalf !== 0) {
        filters.push({
            property: "event.half",
            op: "equal",
            constant: stateFilters.selectedHalf,
        })
    }

    if (
        stateFilters.selectedInning &&
        stateFilters.selectedInning !== "all-innings"
    ) {
        findOrAddTagFilter(filters, {
            property: "event.tags",
            op: "tagged",
            constant: [stateFilters.selectedInning],
        })
    }

    if (
        stateFilters.selectedBaseballSide &&
        stateFilters.selectedBaseballSide !== "all"
    ) {
        findOrAddTagFilter(filters, {
            property: "event.tags",
            op: "tagged",
            constant: [stateFilters.selectedBaseballSide],
        })
    }

    if (
        stateFilters.selectedBaseballTag &&
        stateFilters.selectedBaseballTag !== "all"
    ) {
        findOrAddTagFilter(filters, {
            property: "event.tags",
            op: "tagged",
            constant: [stateFilters.selectedBaseballTag],
        })
    }

    if (stateFilters.selectedCamera && stateFilters.selectedCamera !== "all") {
        filters.push({
            property: "",
            op: "camera",
            constant: stateFilters.selectedCamera,
        })
    }

    return filters
}

export const buildFilterOptions = (availableFilters, allOptions) => {
    const alwaysAvailable = ["all", "all-innings", 0]

    if (availableFilters.length === 0 || allOptions === null) {
        return allOptions
    }

    return allOptions.map((opt) => {
        return {
            ...opt,
            disabled: !(
                availableFilters.includes(opt.value) ||
                alwaysAvailable.includes(opt.value)
            ),
        }
    })
}

export const getAvailableFilters = (filterType, playlistEvents) => {
    let availableFilters = new Set()

    if (playlistEvents === null || playlistEvents === undefined) {
        return []
    }

    switch (filterType) {
        case "half":
            for (const event of playlistEvents) {
                availableFilters.add(event.half)
            }
            break
        case "tags":
            for (const event of playlistEvents) {
                for (const tag of event.tags) {
                    availableFilters.add(tag.name)
                }
            }
            break
        default:
            return []
    }

    return [...availableFilters.values()]
}

export const generatePlaylists = (
    templates,
    coaches,
    players,
    events,
    goalies,
    renderType
) => {
    if (!goalies) {
        throw new Error("no goalies")
    }
    var lists = []

    for (var key in templates) {
        var tmpl = templates[key]
        if (tmpl.hide_for_sensorless && renderType === "sensorless") {
            tmpl.enabled = false
        }
        if (tmpl.hide_for_sensors && renderType !== "sensorless") {
            tmpl.enabled = false
        }

        if (tmpl.scopes.includes("coach")) {
            var coachScope = {
                coach: {
                    id: 0,
                    name: "",
                },
            }
            lists.push(generatePlaylist(tmpl, coachScope, events))
        }
        if (tmpl.scopes.includes("player")) {
            var sublist = []
            for (let i in players) {
                if (players[i]) {
                    let player = Object.assign({}, players[i], {
                        first_name: formatFirstName(players[i]),
                        last_name: formatLastName(players[i]),
                    })
                    if (player.first_name === "Tracer") {
                        player.last_name = player.id
                    }
                    let playerGoalies = goalies.filter(
                        (g) => g.id === player.id
                    )
                    let scope = {
                        player: player,
                        players: players,
                        goalies: playerGoalies,
                    }
                    sublist.push(generatePlaylist(tmpl, scope, events))
                }
            }
            //Sort by title
            sublist = sublist.sort((a, b) => {
                if (a.title.includes("Tracer")) {
                    return 1
                }
                if (b.title.includes("Tracer")) {
                    return -1
                }
                return a.title.localeCompare(b.title)
            })
            lists.push.apply(lists, sublist)
        }
        if (tmpl.scopes.includes("camera")) {
            let cameras = {}

            for (let event of events) {
                if (event.meta.camera) {
                    cameras[event.meta.camera.name] = event.meta.camera
                }
            }

            var sublist = []
            for (let i in cameras) {
                let camera = cameras[i]
                let scope = { camera: camera, cameras: cameras }
                sublist.push(generatePlaylist(tmpl, scope, events))
            }
            //Sort by title
            sublist = sublist.sort((a, b) => {
                if (a.id.includes("multi")) {
                    return -1
                }
                if (b.id.includes("multi")) {
                    return 1
                }
                return a.title.localeCompare(b.title)
            })
            lists.push.apply(lists, sublist)
        }
        if (tmpl.scopes.length === 0) {
            lists.push(generatePlaylist(tmpl, { players: players }, events))
        }
    }
    return lists
}

function generatePlaylist(tmpl, scope, events) {
    let id = renderString(tmpl.id, scope)
    let title = renderString(tmpl.title, scope)
    var playlist = Object.assign({}, tmpl, {
        id: id,
        title: title,
        scope: scope,
        event_count: 0,
        show_when_empty: tmpl.show_when_empty,
    })

    var filteredEvents = filterEventsWithPlaylist(playlist, events)
    playlist.event_count = filteredEvents.length
    return playlist
}

function callConverter(funcName, scope) {
    let value = ""
    let subs = funcName.split("+")
    let idx = 0
    const numerator = ["1st", "2nd", "3rd", "4th", "5th"]

    switch (subs[0]) {
        case "max_effort":
            if (scope.event.meta.max_efforts) {
                for (let i in scope.event.meta.trace_numbers) {
                    if (scope.event.meta.trace_numbers[i] === scope.player.id) {
                        if (scope.event.meta.max_efforts[i] === 1) {
                            value = ", max effort"
                        }
                        break
                    }
                }
            }
            break
        case "nth_in_chain":
            for (let i in scope.event.meta.trace_numbers) {
                if (scope.event.meta.trace_numbers[i] === scope.player.id) {
                    idx = i
                    break
                }
            }
            value =
                numerator[idx] +
                " in a " +
                scope.event.meta.trace_numbers.length +
                " chain"
            break
        case "disabled thirds":
            if (scope.event.meta.thirds) {
                if (scope.event.meta.thirds[0] === scope.event.meta.thirds[1]) {
                    value = `(${scope.event.meta.thirds[0]} 3rd)`
                } else {
                    value = `(${scope.event.meta.thirds[0]} 3rd to ${scope.event.meta.thirds[1]} 3rd)`
                }
            }
            break
        case "chain_names":
            if (scope.players && scope.event.meta.trace_numbers) {
                for (let i in scope.event.meta.trace_numbers) {
                    let tn = scope.event.meta.trace_numbers[i]
                    let player = { first_name: `Tracer ${tn}`, last_name: "" }
                    let isGoalie = false
                    if (scope.event.meta.goalies) {
                        for (let gn of scope.event.meta.goalies) {
                            if (gn === tn) {
                                isGoalie = true
                                break
                            }
                        }
                    }
                    if (!isGoalie) {
                        for (let p of scope.players) {
                            if (p.trace_number === tn) {
                                player = p
                                break
                            }
                        }
                        if (value) value += " \u2758 "
                        value += formatFullName(player)
                        let oldplayer = scope.player
                        for (let j = 1; j < subs.length; j++) {
                            scope.player = player
                            value += " " + callConverter(subs[j], scope)
                        }
                        scope.player = oldplayer
                    }
                }
            }
            break
        default:
            break
    }
    return value
}

function getBaseballTagsSortValue(event) {
    let value = 1111

    let hitTag = event.tags?.find((tag) => tag.name === "hit")
    let outTag = event.tags?.find((tag) => tag.name === "out")
    let defTag = event.tags?.find((tag) => tag.name === "defensive")
    let pitTag = event.tags?.find((tag) => tag.name === "pitcher")

    if (hitTag) {
        value -= 1000
    }
    if (outTag) {
        value -= 100
    }
    if (defTag) {
        value -= 10
    }
    if (pitTag) {
        value -= 1
    }
    return value
}

/**
 * Used for rounding floating point values like distance to 1 fraction digit
 * Skips non-float values and returns them as is
 * @param value
 * @returns {*}
 */
function valueRound(value) {
    let fvalue = parseFloat(value)
    if (isNaN(fvalue)) return value
    return Math.round(fvalue * 10) / 10
}

function renderString(tmplStr, scope) {
    /*eslint-disable */
    var patt = /\{\{([\w\.]+)\}\}/g
    var funcPatt = /\[\[([\w\+]+)\]\]/g
    let result = tmplStr.replace(patt, (a, val) =>
        valueRound(valueFor(val, "", scope))
    )
    if (result !== undefined)
        result = result.replace(funcPatt, (a, val) => callConverter(val, scope))
    /*eslint-enable */
    return result
}
